/**
 * WHERE A PHOTO WAS TAKEN, CARRIED THROUGH THE SHRINK.
 *
 * Anthony, 2026-10-27: original photos keep their real location, exact,
 * including the ones taken in a customer's driveway. shrink() redraws the
 * photo through a canvas and a canvas writes a JPEG with no EXIF at all, so
 * every photo a technician took lost the spot it was taken the moment it
 * was made small enough to send.
 *
 * So the GPS is read off the file the phone handed over, before the shrink,
 * and written back into the copy that is uploaded.
 *
 * ONLY LATITUDE AND LONGITUDE GO BACK IN. The phone model, its serial, the
 * owner's name and the rest of what a camera writes stay on the phone. The
 * order was the location, not the whole block.
 *
 * Same logic as MechanicRank's photo-gps.js, which shipped first. Kept free
 * of the database client so a test can run it in plain Node.
 */

export type Gps = { lat: number; lng: number };

/**
 * Where the TIFF header starts, or -1. Scanning for "Exif\0\0" rather than
 * walking JPEG segments is what makes one reader work for a JPEG, a WebP
 * and an iPhone HEIC, which all carry the same block in different boxes.
 */
function findTiff(b: Uint8Array): number {
  for (let i = 0; i + 10 < b.length; i++) {
    if (b[i] !== 0x45 || b[i + 1] !== 0x78 || b[i + 2] !== 0x69 || b[i + 3] !== 0x66
      || b[i + 4] !== 0 || b[i + 5] !== 0) continue;
    const t = i + 6;
    if ((b[t] === 0x49 && b[t + 1] === 0x49 && b[t + 2] === 42 && b[t + 3] === 0)
      || (b[t] === 0x4d && b[t + 1] === 0x4d && b[t + 2] === 0 && b[t + 3] === 42)) return t;
  }
  return -1;
}

/** IFD0's GPS pointer (0x8825), then tags 1-4 of the GPS IFD. */
function gpsFromTiff(b: Uint8Array, t: number): Gps | null {
  const dv = new DataView(b.buffer, b.byteOffset + t, b.length - t);
  const le = b[t] === 0x49;
  const u16 = (o: number): number => dv.getUint16(o, le);
  const u32 = (o: number): number => dv.getUint32(o, le);

  const ifd0 = u32(4);
  let gps = 0;
  for (let k = 0, n = u16(ifd0); k < n; k++) {
    const e = ifd0 + 2 + k * 12;
    if (u16(e) === 0x8825) gps = u32(e + 8);
  }
  if (!gps) return null;

  const tags = new Map<number, number>();
  for (let j = 0, m = u16(gps); j < m; j++) {
    const g = gps + 2 + j * 12;
    tags.set(u16(g), g);
  }
  // A two-byte ASCII ref fits inside the entry itself, so its value is the
  // first byte of the value field rather than an offset.
  const ref = (tag: number): string => {
    const at = tags.get(tag);
    return at ? String.fromCharCode(dv.getUint8(at + 8)) : "";
  };
  // Three rationals: degrees, minutes, seconds.
  const deg = (tag: number): number | null => {
    const at = tags.get(tag);
    if (!at) return null;
    const off = u32(at + 8);
    let v = 0;
    let scale = 1;
    for (let r = 0; r < 3; r++) {
      const num = u32(off + r * 8);
      const den = u32(off + r * 8 + 4);
      if (!den) return null;
      v += num / den / scale;
      scale *= 60;
    }
    return v;
  };

  let lat = deg(2);
  let lng = deg(4);
  // 0,0 is what a phone writes when it had no fix, not a photo in the sea.
  if (lat === null || lng === null || (lat === 0 && lng === 0)) return null;
  if (ref(1) === "S") lat = -lat;
  if (ref(3) === "W") lng = -lng;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

/** The GPS in these bytes, or null. Never throws. */
export function gpsOfBytes(b: Uint8Array): Gps | null {
  const t = findTiff(b);
  if (t < 0) return null;
  try { return gpsFromTiff(b, t); } catch { return null; }
}

/** The GPS in a photo file, or null when it has none or cannot be read. */
export async function readGps(file: Blob): Promise<Gps | null> {
  try {
    return gpsOfBytes(new Uint8Array(await file.arrayBuffer()));
  } catch {
    return null;
  }
}

/**
 * A minimal little-endian TIFF: IFD0 holds one entry, the GPS pointer; the
 * GPS IFD holds version, N/S, latitude, E/W, longitude. Seconds are stored
 * to a ten-thousandth, which is about 3mm, so "exact" stays exact.
 */
function gpsTiff(gps: Gps): Uint8Array<ArrayBuffer> {
  const buf = new ArrayBuffer(8 + 18 + 66 + 48);
  const dv = new DataView(buf);
  const u8 = new Uint8Array(buf);
  u8.set([0x49, 0x49, 42, 0]);
  dv.setUint32(4, 8, true);
  dv.setUint16(8, 1, true);
  dv.setUint16(10, 0x8825, true); dv.setUint16(12, 4, true);
  dv.setUint32(14, 1, true); dv.setUint32(18, 26, true);
  dv.setUint32(22, 0, true);

  const g = 26;
  const data = g + 2 + 5 * 12 + 4;
  dv.setUint16(g, 5, true);
  const entry = (i: number, tag: number, type: number, count: number): number => {
    const e = g + 2 + i * 12;
    dv.setUint16(e, tag, true); dv.setUint16(e + 2, type, true); dv.setUint32(e + 4, count, true);
    return e + 8;
  };
  u8.set([2, 3, 0, 0], entry(0, 0, 1, 4));
  u8[entry(1, 1, 2, 2)] = (gps.lat >= 0 ? "N" : "S").charCodeAt(0);
  dv.setUint32(entry(2, 2, 5, 3), data, true);
  u8[entry(3, 3, 2, 2)] = (gps.lng >= 0 ? "E" : "W").charCodeAt(0);
  dv.setUint32(entry(4, 4, 5, 3), data + 24, true);
  dv.setUint32(g + 2 + 5 * 12, 0, true);

  [Math.abs(gps.lat), Math.abs(gps.lng)].forEach((v, i) => {
    const d = Math.floor(v);
    const mf = (v - d) * 60;
    const mi = Math.floor(mf);
    const s = Math.round((mf - mi) * 60 * 10000);
    const o = data + i * 24;
    dv.setUint32(o, d, true); dv.setUint32(o + 4, 1, true);
    dv.setUint32(o + 8, mi, true); dv.setUint32(o + 12, 1, true);
    dv.setUint32(o + 16, s, true); dv.setUint32(o + 20, 10000, true);
  });
  return u8;
}

/**
 * The same JPEG with a GPS-only EXIF block straight after SOI. Anything
 * that is not a JPEG, or no GPS to write, comes back untouched: a photo
 * without its location still beats no photo.
 */
export async function injectGps(jpeg: Blob, gps: Gps | null): Promise<Blob> {
  if (!gps) return jpeg;
  try {
    const ab = await jpeg.arrayBuffer();
    const b = new Uint8Array(ab);
    if (b[0] !== 0xff || b[1] !== 0xd8) return jpeg;
    const tiff = gpsTiff(gps);
    const len = 2 + 6 + tiff.length;
    const head = new Uint8Array([0xff, 0xe1, len >> 8, len & 0xff, 0x45, 0x78, 0x69, 0x66, 0, 0]);
    return new Blob([ab.slice(0, 2), head, tiff, ab.slice(2)], { type: "image/jpeg" });
  } catch {
    return jpeg;
  }
}

/**
 * GPS out of the `exif` object the Camera plugin returns beside the photo,
 * for when the file itself came back without it. The two platforms shape
 * it differently: Android hands the raw EXIF strings ("34/1,3/1,1234/100"
 * and "N"), iOS hands decimal degrees, sometimes under a "{GPS}" key.
 * Anything else reads as no location rather than a wrong one.
 */
export function gpsOfPluginExif(exif: unknown): Gps | null {
  if (!exif || typeof exif !== "object") return null;
  const top = exif as Record<string, unknown>;
  const src = (top["{GPS}"] && typeof top["{GPS}"] === "object")
    ? top["{GPS}"] as Record<string, unknown> : top;
  const pick = (...keys: string[]): unknown => {
    for (const k of keys) if (src[k] !== undefined && src[k] !== null) return src[k];
    return undefined;
  };
  const num = (v: unknown): number | null => {
    if (typeof v === "number") return Number.isFinite(v) ? v : null;
    if (typeof v !== "string" || !v.trim()) return null;
    // "34/1,3/1,1234/100" is degrees, minutes, seconds as rationals.
    if (v.includes("/")) {
      let out = 0;
      let scale = 1;
      for (const part of v.split(",").slice(0, 3)) {
        const [n = NaN, d = 0] = part.split("/").map(Number);
        if (!d || !Number.isFinite(n)) return null;
        out += n / d / scale;
        scale *= 60;
      }
      return out;
    }
    const f = Number(v);
    return Number.isFinite(f) ? f : null;
  };

  let lat = num(pick("GPSLatitude", "Latitude"));
  let lng = num(pick("GPSLongitude", "Longitude"));
  if (lat === null || lng === null || (lat === 0 && lng === 0)) return null;
  const latRef = String(pick("GPSLatitudeRef", "LatitudeRef") ?? "").toUpperCase();
  const lngRef = String(pick("GPSLongitudeRef", "LongitudeRef") ?? "").toUpperCase();
  if (latRef.startsWith("S")) lat = -Math.abs(lat);
  if (lngRef.startsWith("W")) lng = -Math.abs(lng);
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}
